import React, { useEffect, useState } from "react";
import { GoogleMap, OverlayView, useJsApiLoader } from "@react-google-maps/api";
import { Home, Building2, Briefcase, Store, Tag, Truck, Fuel } from "lucide-react";
import GasCylinderIcon from "./icons/GasCylinderIcon";

const mapKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBS = ["places", "drawing"];

// Map an address `type` to its lucide icon. Falls back to Home when missing.
const ADDRESS_ICON = {
  casa: Home,
  departamento: Building2,
  oficina: Briefcase,
  negocio: Store,
  otro: Tag,
};
const channelIconFor = (c) => (c === "autotanque" ? Truck : c === "estacion" ? Fuel : GasCylinderIcon);

/**
 * 3D pulsing pin rendered as HTML inside an OverlayView so it can use the
 * shared `.map-pin-pulse` animation (same look & feel as MapPicker).
 *
 * @param {Object} props
 * @param {{lat:number,lng:number}} props.position
 * @param {React.ComponentType} props.Icon
 * @param {string} props.color  Pin background color (company primary).
 * @param {string} props.contrast  Icon foreground (company primary contrast).
 * @param {"sm"|"md"} [props.size]  md = 48px head, sm = 40px head.
 */
function PulsingPin({ position, Icon, color, contrast, size = "md" }) {
  const head = size === "sm" ? 40 : 48;
  const iconSize = size === "sm" ? 18 : 22;
  // OverlayView places the rendered HTML at the geographic position; we
  // translate it so the centre of the pin sits exactly on the coordinate
  // (we removed the tail/diamond so the head IS the pin).
  const offset = { x: -(head / 2), y: -(head / 2) };
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={() => offset}
    >
      <div
        className="pointer-events-none"
        style={{ color, height: head, width: head }}
      >
        <div className="relative" style={{ height: head, width: head }}>
          <span
            className="absolute inset-0 rounded-full map-pin-pulse"
            aria-hidden
          />
          <div
            className="relative rounded-full flex items-center justify-center ring-2 ring-white"
            style={{
              height: head,
              width: head,
              backgroundColor: color,
              color: contrast,
              boxShadow:
                "0 4px 10px rgba(0,0,0,0.25), inset 0 -3px 6px rgba(0,0,0,0.2), inset 0 3px 6px rgba(255,255,255,0.25)",
            }}
          >
            <Icon size={iconSize} strokeWidth={2.4} />
          </div>
        </div>
      </div>
    </OverlayView>
  );
}

export default function TrackingMap({
  driverLocation,
  destination,
  channel = "cilindrera",
  addressType,
  color,
  contrast = "#ffffff",
  height = 320,
  interactive = true,
  status,
  onEtaChange,
  serviceAreaPolygons = null,
}) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: mapKey || "", libraries: LIBS });
  const [route, setRoute] = useState(null);

  const primary = color || "#111";
  const onTheWay = status === "on_the_way";
  const [mapRef, setMapRef] = useState(null);
  // Track which status we already auto-fit the camera for. We re-fit ONLY on
  // status transitions (pending→confirmed, confirmed→on_the_way, …) so the
  // user's pan/zoom is preserved across the 8s tracking polls within the
  // same status, but the camera adapts when the journey reaches a new step.
  const [fittedFor, setFittedFor] = useState(null);

  // Imperatively manage the route polyline via google.maps directly. This
  // sidesteps a known issue in @react-google-maps/api where conditionally
  // unmounted <Polyline /> components can leave the underlying overlay
  // attached to the map (so the trace would linger past on_the_way → delivered).
  useEffect(() => {
    if (!mapRef || !window.google?.maps) return;
    if (!onTheWay || !route) return;
    const poly = new window.google.maps.Polyline({
      path: route,
      strokeColor: primary,
      strokeOpacity: 0.85,
      strokeWeight: 5,
      geodesic: true,
      map: mapRef,
    });
    return () => {
      try { poly.setMap(null); } catch (_e) { /* ignore */ }
    };
  }, [mapRef, onTheWay, route, primary]);

  // We ask for directions only while the order is in transit. Confirmed and
  // delivered don't need a route trace. We also use a `cancelled` flag so a
  // late Directions callback can't re-populate `route` after we've already
  // moved past the on_the_way status (otherwise the trace would linger on
  // the delivered map until a manual refresh).
  useEffect(() => {
    if (!onTheWay) {
      setRoute(null);
      try { onEtaChange && onEtaChange(null); } catch (_e) { /* ignore */ }
      return;
    }
    if (!isLoaded || !driverLocation || !destination || !window.google?.maps) return;
    let cancelled = false;
    const svc = new window.google.maps.DirectionsService();
    svc.route(
      {
        origin: driverLocation,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, st) => {
        if (cancelled) return;
        if (st === "OK" && result?.routes?.[0]) {
          setRoute(result.routes[0].overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() })));
          // Surface ETA to the parent so it can apply company-tolerance
          // and render the user-facing label. We pass duration_in_traffic
          // when available so it accounts for current congestion.
          const leg = result.routes[0].legs?.[0];
          if (leg && onEtaChange) {
            const dur = leg.duration_in_traffic || leg.duration;
            try {
              onEtaChange({ seconds: dur?.value ?? null, distance_m: leg.distance?.value ?? null });
            } catch (_e) { /* ignore */ }
          }
        } else {
          setRoute(null);
          try { onEtaChange && onEtaChange(null); } catch (_e) { /* ignore */ }
        }
      },
    );
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, onTheWay, driverLocation?.lat, driverLocation?.lng, destination?.lat, destination?.lng]);

  // Re-fit the camera when the status transitions. We only run the fit logic
  // when `fittedFor !== status`, so within a given status pan/zoom by the
  // user is respected. When `on_the_way` arrives but the driver hasn't
  // reported yet, we centre on the destination so the user immediately sees
  // *something* — and re-fit both pins as soon as the driver shows up.
  useEffect(() => {
    if (!mapRef || !window.google?.maps) return;
    if (fittedFor === status) return;
    if (onTheWay) {
      if (driverLocation && destination) {
        const b = new window.google.maps.LatLngBounds();
        b.extend(driverLocation);
        b.extend(destination);
        mapRef.fitBounds(b, 64);
        setFittedFor(status);
      } else if (destination) {
        // Provisional view while the driver hasn't reported. Don't mark the
        // status as fitted yet so the next render with a driver triggers fit.
        mapRef.panTo(destination);
        mapRef.setZoom(15);
      }
    } else if (destination) {
      mapRef.panTo(destination);
      mapRef.setZoom(15);
      setFittedFor(status);
    }
  }, [mapRef, status, fittedFor, onTheWay, driverLocation?.lat, driverLocation?.lng, destination?.lat, destination?.lng]); // eslint-disable-line

  // Optional service-area polygons — drawn imperativamente para que el
  // overlay se desmonte limpio cuando el operador apaga el toggle (mismo
  // patrón que la polyline de la ruta). Cuando se enciende, ajustamos el
  // encuadre del mapa para incluir tanto los polígonos como el destino,
  // así el operador ve la zona completa de un solo vistazo.
  useEffect(() => {
    if (!mapRef || !window.google?.maps) return;
    if (!serviceAreaPolygons || serviceAreaPolygons.length === 0) return;
    const polys = serviceAreaPolygons.map((ring) => new window.google.maps.Polygon({
      paths: (ring || []).map(([lat, lng]) => ({ lat, lng })),
      strokeColor: primary,
      strokeOpacity: 0.85,
      strokeWeight: 2,
      fillColor: primary,
      fillOpacity: 0.12,
      clickable: false,
      map: mapRef,
    }));
    // Encuadre que incluye polígonos + destino + driver (si existe).
    try {
      const b = new window.google.maps.LatLngBounds();
      serviceAreaPolygons.forEach((ring) => (ring || []).forEach(([lat, lng]) => b.extend({ lat, lng })));
      if (destination) b.extend(destination);
      if (driverLocation) b.extend(driverLocation);
      if (!b.isEmpty()) mapRef.fitBounds(b, 64);
    } catch (_e) { /* ignore */ }
    return () => {
      polys.forEach((p) => { try { p.setMap(null); } catch (_e) { /* ignore */ } });
    };
  }, [mapRef, serviceAreaPolygons, primary, destination?.lat, destination?.lng, driverLocation?.lat, driverLocation?.lng]); // eslint-disable-line

  // Cuando el operador apaga el toggle de cobertura, regresamos el
  // encuadre al destino (o driver+destino si está en camino), porque el
  // efecto principal de auto-fit sólo dispara en cambios de status.
  useEffect(() => {
    if (!mapRef || !window.google?.maps) return;
    if (serviceAreaPolygons && serviceAreaPolygons.length > 0) return;
    if (!destination) return;
    if (onTheWay && driverLocation) {
      const b = new window.google.maps.LatLngBounds();
      b.extend(driverLocation);
      b.extend(destination);
      mapRef.fitBounds(b, 64);
    } else {
      mapRef.panTo(destination);
      mapRef.setZoom(15);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, serviceAreaPolygons]);

  if (!mapKey) {
    return (
      <div className="rounded-xl bg-neutral-100 p-6 text-sm text-neutral-600 text-center">
        Agrega la API key de Google Maps para ver el mapa en vivo.
      </div>
    );
  }
  if (!isLoaded) return <div className="bg-neutral-100 rounded-xl animate-pulse" style={{ height }} />;

  // Pick the destination icon from the saved address `type` field.
  const DestIcon = ADDRESS_ICON[(addressType || "").toLowerCase()] || Home;
  const DriverIcon = channelIconFor(channel);

  // Decide what to render:
  //   confirmed / delivered → only destination pin (with pulse)
  //   on_the_way            → both pins (route polyline is managed imperatively)
  //   pending / cancelled   → caller is expected not to render the map at all,
  //                           but we still degrade gracefully.
  const showDriver = onTheWay && !!driverLocation;

  return (
    <div className="rounded-xl overflow-hidden border border-neutral-200 fade-in">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height }}
        onLoad={(m) => setMapRef(m)}
        options={{
          disableDefaultUI: !interactive,
          zoomControl: interactive,
          gestureHandling: interactive ? "auto" : "none",
          clickableIcons: false,
          styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
          ],
        }}
      >
        {destination && (
          <PulsingPin
            position={destination}
            Icon={DestIcon}
            color={primary}
            contrast={contrast}
            size="md"
          />
        )}
        {showDriver && (
          <PulsingPin
            position={driverLocation}
            Icon={DriverIcon}
            color={primary}
            contrast={contrast}
            size="sm"
          />
        )}
      </GoogleMap>
    </div>
  );
}
